import type {
  OutputData,
  TriggerOutputData,
} from "@smartsuite/automation-engine-api";
import { useRootStore } from "../../../../../stores/hooks";
import {
  INPUT_APPLICATION_ID,
  OUTPUT_APPLICATION_ID,
  OUTPUT_TITLE,
  RECORD_ID,
} from "../../../../../utils/constants";
import { pickValueDataString } from "../../../../../utils/inputs";

interface TimeFunctions {
  getSolutionId: () => string;
  getSolutionName: () => string;
  getApplicationId: () => string;
  getApplicationName: () => string;
  getRecordUrl: () => string;
  getRecordId: () => string;
  getTitle: () => string;
}

export const useTriggerProps = (trigger?: TriggerOutputData): TimeFunctions => {
  const { solutions, applications, config, automations } = useRootStore();

  const getSolutionId = (): string => config.solutionId;
  const getAccountId = (): string => config.accountId;
  const getRecordId = (): string => findInputValue(RECORD_ID);
  const getTitle = (): string => findInputValue(OUTPUT_TITLE);

  const getSolutionName = (): string => {
    const solutionId = getSolutionId();
    const solution = solutions.list.find(
      (solution) => solution.id === solutionId,
    );
    if (solution === undefined) {
      return "";
    }
    return solution.name;
  };

  const getApplicationId = (): string => {
    const input = automations.candidate.self.trigger.inputs.find(
      (input) => input.inputId === INPUT_APPLICATION_ID,
    );
    if (input?.value !== undefined) {
      const value = pickValueDataString(input.value);
      return value ?? "";
    }
    return findInputValue(OUTPUT_APPLICATION_ID);
  };

  const getApplicationName = (): string => {
    const applicationId = getApplicationId();
    const application = applications.list.find(
      (application) => application.id === applicationId,
    );
    if (application === undefined) {
      return "";
    }
    return application.label;
  };

  const getRecordUrl = (): string =>
    `/${getAccountId()}/solution/${getSolutionId()}/${getApplicationId()}?editRecord=${findInputValue(
      RECORD_ID,
    )}`;

  const findInputValue = (outputId: string): string => {
    if (trigger === undefined) {
      return "";
    }
    const outputs = getTriggerOutputs(trigger);
    const output = outputs.find((output) => output.outputId === outputId);
    if (output?.value === undefined) {
      return "";
    }
    const value = pickValueDataString(output.value);
    return value ?? "";
  };

  const getTriggerOutputs = (trigger: TriggerOutputData): OutputData[] => {
    let outputs = [...trigger.outputs];
    if (trigger.recordList?.recordList !== undefined) {
      outputs = [
        ...outputs,
        ...trigger.recordList.recordList.records.flatMap(
          (record) => record.outputs,
        ),
      ];
    }
    return outputs;
  };

  return {
    getSolutionId,
    getSolutionName,
    getApplicationId,
    getApplicationName,
    getRecordId,
    getRecordUrl,
    getTitle,
  };
};
